<script setup lang="ts">
  import { ROUTES } from '@/router/routes'
  import PaginatedTableView from '@/components/admin/PaginatedTable.vue'
  import type { PaginatedResponse } from '@/components/admin/PaginatedTable.vue'
  import { type SuperUserOrganisation as Organisation } from '@/types/generated/types.gen'
  import { OrganisationsService } from '@/types/generated/services.gen'
  import type { FetchDataOptions } from '@/components/admin/PaginatedTable.vue'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import TableOptionsButton from '@/components/admin/table/TableOptionsButton.vue'
  import CustomerDisplay from '@/components/admin/CustomerDisplay.vue'
  import { TableCell, TableRow } from '@/components/ui/table'
  import LoginAsModal from './LoginAsModal.vue'
  import { LogIn, AlertTriangle, CreditCard } from 'lucide-vue-next'
  import { ref, watch, nextTick, onMounted } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import SelectField from '@/components/admin/form/SelectField.vue'

  const route = useRoute()
  const router = useRouter()

  const loginAsModalOpen = ref(false)
  const selectedOrganisation = ref<Organisation | null>(null)

  const openLoginAsModal = (org: Organisation) => {
    selectedOrganisation.value = org
    loginAsModalOpen.value = true
  }

  const redirectTo = ref('')
  const filterStripe = ref<string | null>(null)
  const filterHubspot = ref<string | null>(null)
  const filterPhonebot = ref<string | null>(null)
  const filterPlatform = ref<string | null>(null)
  const filterIntegrityErrors = ref<string | null>(null)
  const filterPlatformSubscription = ref<string | null>(null)
  const filterPhonebotSubscription = ref<string | null>(null)
  const tableRef = ref<InstanceType<typeof PaginatedTableView> | null>(null)

  // Initialize filters from URL query params
  const initFiltersFromUrl = () => {
    const {
      stripe,
      hubspot,
      phonebot,
      platform,
      integrity_errors,
      platform_subscription,
      phonebot_subscription
    } = route.query
    if (stripe === 'true' || stripe === 'false') filterStripe.value = stripe
    if (hubspot === 'true' || hubspot === 'false') filterHubspot.value = hubspot
    if (phonebot === 'self_service' || phonebot === 'managed') filterPhonebot.value = phonebot
    if (platform === 'true' || platform === 'false') filterPlatform.value = platform
    if (integrity_errors === 'true' || integrity_errors === 'false')
      filterIntegrityErrors.value = integrity_errors
    if (platform_subscription === 'true' || platform_subscription === 'false')
      filterPlatformSubscription.value = platform_subscription
    if (phonebot_subscription === 'true' || phonebot_subscription === 'false')
      filterPhonebotSubscription.value = phonebot_subscription
  }

  // Update URL when filters change
  const updateUrlWithFilters = () => {
    const query: Record<string, string> = {}
    if (filterStripe.value !== null) query.stripe = filterStripe.value
    if (filterHubspot.value !== null) query.hubspot = filterHubspot.value
    if (filterPhonebot.value !== null) query.phonebot = filterPhonebot.value
    if (filterPlatform.value !== null) query.platform = filterPlatform.value
    if (filterIntegrityErrors.value !== null) query.integrity_errors = filterIntegrityErrors.value
    if (filterPlatformSubscription.value !== null)
      query.platform_subscription = filterPlatformSubscription.value
    if (filterPhonebotSubscription.value !== null)
      query.phonebot_subscription = filterPhonebotSubscription.value

    router.replace({ query })
  }

  // Initialize on mount
  onMounted(() => {
    initFiltersFromUrl()
  })

  const booleanFilterOptions = [
    { value: null, title: 'All' },
    { value: 'true', title: 'Yes' },
    { value: 'false', title: 'No' }
  ]

  const phonebotFilterOptions = [
    { value: null, title: 'All' },
    { value: 'self_service', title: 'Self-Service' },
    { value: 'managed', title: 'Managed' }
  ]

  // Helper to get phonebot display text
  const getPhonebotType = (org: Organisation): string | null => {
    if (org.is_phonebot_self_service_customer) {
      return 'Self-Service'
    } else if (org.enable_voice) {
      return 'Managed'
    }
    return null
  }

  // Helper to get language display text
  const getLanguageDisplay = (org: Organisation): string => {
    const languageMap: Record<string, string> = {
      de: 'Deutsch',
      fr: 'Französisch',
      en: 'Englisch'
    }
    return languageMap[org.default_language ?? 'de'] ?? 'Deutsch'
  }

  // Watch for filter changes and refresh the table
  watch(
    [
      filterStripe,
      filterHubspot,
      filterPhonebot,
      filterPlatform,
      filterIntegrityErrors,
      filterPlatformSubscription,
      filterPhonebotSubscription
    ],
    async () => {
      updateUrlWithFilters()
      await nextTick()
      tableRef.value?.resetAndRefresh()
    }
  )

  const fetchOrganisations = async (
    options?: FetchDataOptions
  ): Promise<PaginatedResponse<unknown>> => {
    const response = await OrganisationsService.organisationsList({
      limit: options?.limit,
      offset: options?.offset,
      search: options?.search,
      hasStripe: filterStripe.value === null ? undefined : filterStripe.value === 'true',
      hasHubspot: filterHubspot.value === null ? undefined : filterHubspot.value === 'true',
      phonebotType: filterPhonebot.value === null ? undefined : filterPhonebot.value,
      hasPlatform: filterPlatform.value === null ? undefined : filterPlatform.value === 'true',
      hasIntegrityErrors:
        filterIntegrityErrors.value === null ? undefined : filterIntegrityErrors.value === 'true',
      hasPlatformSubscription:
        filterPlatformSubscription.value === null
          ? undefined
          : filterPlatformSubscription.value === 'true',
      hasPhonebotSubscription:
        filterPhonebotSubscription.value === null
          ? undefined
          : filterPhonebotSubscription.value === 'true'
    })
    return {
      results: response.results as unknown[],
      next: response.next ?? null,
      previous: response.previous ?? null,
      count: response.count
    }
  }
</script>

<template>
  <div>
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Customers', to: ROUTES.ADMIN.CUSTOMERS.CUSTOMER_LIST }
          ]"
        />

        <div class="flex items-center justify-end gap-x-2">
          <router-link
            :to="ROUTES.ADMIN.CUSTOMERS.CUSTOMER_CREATE"
            data-cy="create-customer-button"
            class="inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-3 py-2
              text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
              focus:ring-purple-500 disabled:pointer-events-none disabled:opacity-50"
          >
            <svg
              class="hidden size-3 flex-shrink-0 sm:block"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M8 1C8.55228 1 9 1.44772 9 2V7L14 7C14.5523 7 15 7.44771 15 8C15 8.55228 14.5523 9 14 9L9 9V14C9 14.5523 8.55228 15 8 15C7.44772 15 7 14.5523 7 14V9.00001L2 9.00001C1.44772 9.00001 1 8.5523 1 8.00001C0.999999 7.44773 1.44771 7.00001 2 7.00001L7 7.00001V2C7 1.44772 7.44772 1 8 1Z"
              ></path>
            </svg>
            Create New Customer
          </router-link>
        </div>
      </div>
      <div
        class="flex flex-col space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm
          dark:border-neutral-700 dark:bg-neutral-800"
      >
        <PaginatedTableView
          ref="tableRef"
          :fetchData="fetchOrganisations"
          searchable-fields="Uuid, customer number, customer name"
        >
          <template #actions>
            <div class="flex items-end gap-3">
              <div class="w-[130px]">
                <SelectField
                  v-model="filterStripe"
                  name="filterStripe"
                  label="Stripe"
                  :options="booleanFilterOptions"
                />
              </div>
              <div class="w-[130px]">
                <SelectField
                  v-model="filterHubspot"
                  name="filterHubspot"
                  label="HubSpot"
                  :options="booleanFilterOptions"
                />
              </div>
              <div class="w-[140px]">
                <SelectField
                  v-model="filterPhonebot"
                  name="filterPhonebot"
                  label="Phonebot"
                  :options="phonebotFilterOptions"
                />
              </div>
              <div class="w-[130px]">
                <SelectField
                  v-model="filterPlatform"
                  name="filterPlatform"
                  label="Platform"
                  :options="booleanFilterOptions"
                />
              </div>
              <div class="w-[150px]">
                <SelectField
                  v-model="filterIntegrityErrors"
                  name="filterIntegrityErrors"
                  label="Integrity Errors"
                  :options="booleanFilterOptions"
                />
              </div>
              <div class="w-[150px]">
                <SelectField
                  v-model="filterPlatformSubscription"
                  name="filterPlatformSubscription"
                  label="Platform Sub"
                  :options="booleanFilterOptions"
                />
              </div>
              <div class="w-[150px]">
                <SelectField
                  v-model="filterPhonebotSubscription"
                  name="filterPhonebotSubscription"
                  label="Phonebot Sub"
                  :options="booleanFilterOptions"
                />
              </div>
            </div>
          </template>
          <template #table-head>
            <TableCell>Name</TableCell>
            <TableCell>Customer number</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Health</TableCell>
            <TableCell>Stripe</TableCell>
            <TableCell>HubSpot</TableCell>
            <TableCell>Platform</TableCell>
            <TableCell>Phonebot</TableCell>
            <TableCell>Language</TableCell>
          </template>
          <template #table-row="{ items }">
            <TableRow
              v-for="org in items"
              :key="org.uuid"
              class="cursor-pointer"
              data-cy="customer-row"
              @click="
                router.push(
                  ROUTES.ADMIN.CUSTOMERS.CUSTOMER_DETAIL.replace(
                    ':uuid',
                    (org as Organisation).uuid
                  )
                )
              "
            >
              <TableCell class="whitespace-nowrap">
                <div class="flex items-center gap-2">
                  <CustomerDisplay :org="org as Organisation" />
                  <AlertTriangle
                    v-if="(org as Organisation).has_integrity_errors"
                    class="h-4 w-4 shrink-0 text-amber-500"
                    title="Integrity errors detected"
                  />
                </div>
              </TableCell>
              <TableCell>
                {{ (org as Organisation).customer_number }}
              </TableCell>
              <TableCell>
                {{ (org as Organisation).email }}
              </TableCell>
              <TableCell>
                {{ (org as Organisation).vitally_health_score ?? 'N/A' }}
              </TableCell>
              <TableCell>
                <span
                  :class="
                    (org as Organisation).stripe_customer_uuid
                      ? 'text-green-700'
                      : 'text-orange-700'
                  "
                >
                  {{ (org as Organisation).stripe_customer_uuid ? 'Yes' : 'No' }}
                </span>
              </TableCell>
              <TableCell>
                <span
                  :class="
                    (org as Organisation).hubspot_company_uuid
                      ? 'text-green-700'
                      : 'text-orange-700'
                  "
                >
                  {{ (org as Organisation).hubspot_company_uuid ? 'Yes' : 'No' }}
                </span>
              </TableCell>
              <TableCell>
                <div
                  v-if="(org as Organisation).enable_cases"
                  class="flex items-center gap-2"
                >
                  <span class="text-green-700">Yes</span>
                  <CreditCard
                    v-if="(org as Organisation).platform_stripe_subscription_uuid"
                    class="h-4 w-4 text-green-600"
                    title="Stripe subscription connected"
                  />
                  <CreditCard
                    v-else
                    class="h-4 w-4 text-amber-500"
                    title="No Stripe subscription"
                  />
                </div>
                <span
                  v-else
                  class="text-orange-700"
                >
                  No
                </span>
              </TableCell>
              <TableCell>
                <div
                  v-if="getPhonebotType(org as Organisation)"
                  class="flex items-center gap-2"
                >
                  <span
                    :class="
                      (org as Organisation).is_phonebot_self_service_customer
                        ? 'text-emerald-600'
                        : 'text-amber-600'
                    "
                  >
                    {{ getPhonebotType(org as Organisation) }}
                  </span>
                  <CreditCard
                    v-if="(org as Organisation).phonebot_stripe_subscription_uuid"
                    class="h-4 w-4 text-green-600"
                    title="Stripe subscription connected"
                  />
                  <CreditCard
                    v-else
                    class="h-4 w-4 text-amber-500"
                    title="No Stripe subscription"
                  />
                </div>
                <span
                  v-else
                  class="text-gray-400"
                >
                  -
                </span>
              </TableCell>
              <TableCell>
                {{ getLanguageDisplay(org as Organisation) }}
              </TableCell>
              <TableCell @click.stop>
                <TableOptionsButton
                  data-cy="customer-options-button"
                  :options="[
                    {
                      label: 'Login As',
                      dataCy: 'menu-login-as',
                      icon: LogIn,
                      onClick: () => openLoginAsModal(org as Organisation)
                    },
                    {
                      label: 'Chatbot',
                      dataCy: 'menu-chatbot',
                      to: ROUTES.ADMIN.CUSTOMERS.CUSTOMER_BOT_SETTINGS.replace(
                        ':uuid',
                        (org as Organisation).uuid
                      )
                    }
                  ]"
                />
              </TableCell>
            </TableRow>
          </template>
        </PaginatedTableView>
      </div>
    </div>
    <!-- Login As Modal -->
    <LoginAsModal
      v-model:open="loginAsModalOpen"
      :organisation="selectedOrganisation"
    />
  </div>
</template>

<style scoped></style>
